import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MessageCircle, Phone, ShieldPlus } from "lucide-react";
import { CLINIC, IMAGES, waLink, callLink } from "../data/clinic";
import { useLang } from "../i18n/LanguageContext";

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const { lang, t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden bg-brand-bg pt-32 pb-16 md:pt-40 md:pb-24"
    >
      <div className="grain-overlay" />
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-[1.1fr_0.9fr] lg:px-8">
        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="mb-6 flex flex-wrap items-center gap-3"
          >
            <span className="rounded-full bg-brand-primary/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-brand-primary">
              {t.hero.badge1}
            </span>
            <span className="rounded-full bg-brand-warm/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-brand-warm">
              {t.hero.badge2}
            </span>
          </motion.div>

          <h1 className="font-display text-[2.4rem] font-normal leading-[1.16] tracking-tight text-brand-ink sm:text-6xl lg:text-7xl">
            {t.hero.lines.map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="show"
                  className="block pb-[0.05em]"
                >
                  {i === 1 ? <em className="not-italic text-brand-primary">{line}</em> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.7 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-brand-inksoft md:text-lg"
          >
            {t.hero.intro}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.7 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <a
              href={waLink(null, lang)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="inline-flex items-center gap-2 rounded-full bg-brand-warm px-7 py-4 text-sm font-semibold text-white transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-brand-warmHover"
            >
              <MessageCircle className="h-5 w-5" strokeWidth={1.75} />
              {t.common.bookWhatsApp}
            </a>
            <a
              href={callLink}
              data-testid="hero-call-btn"
              className="inline-flex items-center gap-2 rounded-full border border-brand-primary px-7 py-4 text-sm font-semibold text-brand-primary transition-colors duration-300 hover:bg-brand-primary hover:text-white"
            >
              <Phone className="h-5 w-5" strokeWidth={1.75} />
              {t.common.callNow}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.15, duration: 0.7 }}
            className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-brand-ink"
          >
            <ShieldPlus className="h-5 w-5 text-brand-warm" strokeWidth={1.75} />
            {t.hero.emergency}
          </motion.div>
        </div>

        <div className="relative">
          <motion.div style={{ y: imgY }} className="relative arch-frame spotlight shadow-2xl shadow-brand-ink/10">
            <img
              src={IMAGES.doctorHero}
              alt="Dr. Vinita Jain, MDS Periodontist and Implantologist at Kesari Dental Clinic, Hisar"
              className="h-[420px] w-full object-cover md:h-[560px]"
              loading="eager"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1, duration: 0.6 }}
            className="absolute -bottom-6 -left-4 rounded-2xl border border-brand-border bg-brand-surface px-5 py-4 shadow-xl md:-left-8"
          >
            <p className="font-display text-3xl text-brand-primary">15+</p>
            <p className="text-xs font-medium uppercase tracking-widest text-brand-inksoft">
              {t.hero.yearsBadge}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
